import React from 'react';
import type { TFunction } from 'i18next';
import { Cpu, BarChart2 } from 'lucide-react';
import type { FindingAccent } from '../sections/FindingCard';
import { FindingCard } from '../sections/FindingCard';
import { SectionDivider } from '../sections/SectionDivider';
import { enumLabel } from '../../../utils/i18n-enums';
import i18n from '../../../i18n';

function aiAccent(prob: number): FindingAccent {
  if (prob >= 80) return 'critical';
  if (prob >= 60) return 'warning';
  if (prob >= 40) return 'info';
  return 'success';
}

/** Editorial renderer used inside Verdict (Tier 4 expanded). */
export function renderAiEditorial(data: any, t: TFunction) {
  if (!data) return null;
  const prob = typeof data.ai_probability === 'number' ? data.ai_probability : (parseFloat(data.ai_probability) || 0);
  const verdict = typeof data.verdict === 'string' ? data.verdict : 'UNKNOWN';
  const verdictText = enumLabel(verdict).replace(/_/g, ' ');
  const indicators = data.indicators_detected || [];
  const imgIndicators = data.image_analysis?.indicators || [];

  const probFmt = prob.toFixed(0);
  const headline = prob >= 60
    ? t('analysis.ai.headlineHigh', { prob: probFmt })
    : prob >= 40
      ? t('analysis.ai.headlineModerate', { prob: probFmt })
      : t('analysis.ai.headlineLow', { prob: probFmt });

  const eyebrowParts = [
    `${t('analysis.ai.verdictLabel')} · ${verdictText}`,
    data.disclosure_detected === true
      ? t('analysis.ai.disclosureFound')
      : data.disclosure_detected === false
        ? t('analysis.ai.noDisclosure')
        : null,
    data.coupling_context?.for_verdict?.confidence_level
      ? `${t('analysis.ai.confidenceLower')} ${enumLabel(data.coupling_context.for_verdict.confidence_level).toLowerCase()}`
      : null,
  ].filter(Boolean);

  let quote: React.ReactNode | undefined;
  if (indicators.length > 0 && indicators[0].evidence) {
    quote = indicators[0].evidence;
  } else if (imgIndicators.length > 0) {
    quote = imgIndicators.slice(0, 3).join(' · ');
  }

  const source = (
    <>
      <BarChart2 size={13} strokeWidth={2} />
      <span>{probFmt}% {t('analysis.ai.probability')}</span>
      {indicators.length > 0 && (
        <>
          <span className="sep">·</span>
          <span>{indicators.length} {t('analysis.ai.indicatorsShort')}</span>
        </>
      )}
    </>
  );

  return (
    <>
      <SectionDivider label={t('analysis.ai.detection')} />
      <FindingCard
        icon={Cpu}
        accent={aiAccent(prob)}
        eyebrow={eyebrowParts.join(' · ')}
        headline={headline}
        quote={quote}
        source={source}
      />
    </>
  );
}

/** Backward-compat. */
export function renderAiDetail(data: any, _t: any) {
  const t = i18n.t.bind(i18n) as TFunction;
  return <>{renderAiEditorial(data, t)}</>;
}

export function renderAiSummary(data: any, t: any) {
  if (!data) return null;
  const prob = typeof data.ai_probability === 'number' ? data.ai_probability : (parseFloat(data.ai_probability) || 0);
  return (
    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
      {prob.toFixed(0)}% AI · {t('pipeline.aiProbabilityLabel')}
    </span>
  );
}
